/**
 * Status board for the Accounts view — a config-driven summary of how many
 * accounts sit in each configured stage / sub-stage, with click-to-filter.
 *
 * Mirrors the CBS portal's "Status of Accounts" board: a full-width CBS Queue
 * shortcut tile (ready-to-build RFC deals, linking to the group-build wizard)
 * above the configured kanban stages grouped into the demo's three
 * implementation phase buckets (Intake / Build / Go-Live — see
 * IMPLEMENTATION_BUCKETS), each bucket heading carrying its own total over
 * per-status click-to-filter rows tinted with the demo's STATUS_TONE colors.
 * Stages that don't belong to a bucket (custom steps, including RFC Received)
 * render as their own tile, and the ACTIVE-phase stage renders as the large
 * green "active book" tile. Account counts come from `account.stageCounts`
 * (server-bucketed via the same `resolveAccountPosition` the table/kanban use),
 * so they always match the list. The CBS Queue badge is a separate count of
 * RFC submissions awaiting group creation (`READY_FOR_CBS_STATUSES`) — not
 * RFC Received accounts, which are already built. Clicking a sub-stage toggles
 * it into the `viewSubStageKey` filter.
 *
 * Internal-portal-only surface — client/tenant portals never render it — so
 * it uses the CBS presentation tokens unconditionally.
 */

import React from 'react';
import {useNavigate} from 'react-router-dom';
import {ArrowRight, ChevronDown, Inbox} from 'lucide-react';
import {useAccountsViewConfig} from '@common-web/hooks/useAccountsViewConfig';
import {
  visibleStages,
  type AccountsSubStageKey,
  type AccountsViewStage,
} from '@common/accountsView';
import {CBS_LIVE_TILE, cbsStatusTone} from '@common-web/components/account/cbsAccountsPresentation';

/**
 * The demo's three implementation phase buckets (2026-07-08 reconciliation).
 * Keyed off canonical sub-stages so the grouping holds regardless of the
 * config's stage keys/labels. RFC Received is bucket-less on purpose (it's
 * already a built group, not a queue item) and renders as its own click-to-
 * filter tile ahead of Intake.
 */
const IMPLEMENTATION_BUCKETS: ReadonlyArray<{
  key: string;
  label: string;
  members: ReadonlySet<AccountsSubStageKey>;
}> = [
  {key: 'intake', label: 'Intake', members: new Set(['WELCOME_CALL', 'CENSUS_BUILD_OUT'])},
  {
    key: 'build',
    label: 'Build',
    members: new Set(['CENSUS_REVIEW', 'CENSUS_UPDATE_NEEDED', 'CENSUS_APPROVED']),
  },
  {
    key: 'go-live',
    label: 'Go-Live',
    members: new Set([
      'IMPLEMENTATION_CALL',
      'DRY_RUN_1',
      'FINANCIAL_CALL',
      'DRY_RUN_2',
      'GO_LIVE_RUN',
    ]),
  },
];

/** A rendered board cell: one bucket of stages, or a single bucket-less stage. */
interface BoardGroup {
  key: string;
  label: string;
  stages: AccountsViewStage[];
}

/**
 * Group the pipeline stages into board cells, preserving config order. A stage
 * joins a bucket only when every one of its sub-stages maps into that bucket
 * (exotic configs whose stages span buckets keep their own tile). Consecutive
 * same-bucket stages merge into one cell.
 */
function groupPipelineLanes(lanes: AccountsViewStage[]): BoardGroup[] {
  const groups: BoardGroup[] = [];
  const usedKeys = new Set<string>();
  for (const stage of lanes) {
    const bucket = IMPLEMENTATION_BUCKETS.find((b) =>
      stage.subStages.every((s) => b.members.has(s.systemSubStage)),
    );
    if (!bucket) {
      groups.push({key: `stage:${stage.key}`, label: stage.label, stages: [stage]});
      continue;
    }
    const last = groups[groups.length - 1];
    if (last && last.key.startsWith(`bucket:${bucket.key}`)) {
      last.stages.push(stage);
      continue;
    }
    // Uniquify in case a degenerate config interleaves buckets.
    let key = `bucket:${bucket.key}`;
    for (let n = 2; usedKeys.has(key); n++) key = `bucket:${bucket.key}-${n}`;
    usedKeys.add(key);
    groups.push({key, label: bucket.label, stages: [stage]});
  }
  return groups;
}

interface AccountsStatusBoardProps {
  stageCounts: Record<string, number>;
  subStageCounts: Record<string, number>;
  selectedKeys: string[];
  onToggle: (subStageKey: string) => void;
  onClear: () => void;
  isLoading?: boolean;
  /** Destination for the CBS Queue banner — the group-build wizard (`/accounts/new`). */
  buildHref: string;
  /**
   * RFC submissions awaiting group creation (CLIENT_APPROVED + MSA_SIGNED).
   * Same source the New Group wizard's "Build from the queue" list uses —
   * not the RFC Received account-stage count.
   */
  queueCount: number;
}

export function AccountsStatusBoard({
  stageCounts,
  subStageCounts,
  selectedKeys,
  onToggle,
  onClear,
  isLoading,
  buildHref,
  queueCount,
}: AccountsStatusBoardProps): React.ReactElement {
  const navigate = useNavigate();
  const config = useAccountsViewConfig();
  const lanes = visibleStages(config);
  const [open, setOpen] = React.useState(true);
  const selected = new Set(selectedKeys);

  // cbs-portal renders the live book as a single large green tile rather than
  // a bucket of sub-stage rows; split it out of the pipeline grid. RFC Received
  // stays in the grid as a click-to-filter status — the CBS Queue banner above
  // is a separate ready-to-build RFC count.
  const pipelineLanes = lanes.filter((s) => s.systemPhase !== 'ACTIVE');
  const activeLanes = lanes.filter((s) => s.systemPhase === 'ACTIVE');
  const groups = groupPipelineLanes(pipelineLanes);

  // Collapsed hint: labels of the sub-stages currently filtering the list.
  const selectedLabels = lanes
    .flatMap((stage) => stage.subStages)
    .filter((sub) => selected.has(sub.key))
    .map((sub) => sub.label);

  return (
    <div className='rounded-xl border bg-card p-5'>
      <div className={`flex items-center justify-between ${open ? 'mb-4' : ''}`}>
        <button
          type='button'
          onClick={() => setOpen((o) => !o)}
          className='flex items-center gap-1.5'
          aria-expanded={open}
        >
          <ChevronDown
            className='h-4 w-4 text-muted-foreground transition-transform'
            style={{transform: open ? 'none' : 'rotate(-90deg)'}}
          />
          <span className='text-sm font-semibold'>Status of Accounts</span>
          {!open && selectedLabels.length > 0 ? (
            <span className='ml-1 text-xs text-muted-foreground'>
              · filtered: {selectedLabels.join(', ')}
            </span>
          ) : null}
          {isLoading ? <span className='ml-1 text-xs text-muted-foreground'>updating…</span> : null}
        </button>
        {selectedKeys.length > 0 ? (
          <button
            type='button'
            onClick={onClear}
            className='text-xs font-semibold text-primary hover:underline'
          >
            Clear status filter
          </button>
        ) : null}
      </div>

      {open ? (
        <>
          <button
            type='button'
            onClick={() => navigate(`${buildHref}?queue=1`)}
            className='mb-3 flex w-full items-center gap-3 rounded-lg border border-primary bg-primary/10 px-4 py-3 text-left transition-all hover:bg-primary/15'
          >
            <Inbox className='h-[18px] w-[18px] shrink-0 text-primary' />
            <span className='flex items-baseline gap-2'>
              <span className='text-[13px] font-semibold text-primary'>CBS Queue</span>
              <span className='text-[12px] text-muted-foreground'>
                {queueCount > 0
                  ? `${queueCount} ${queueCount === 1 ? 'group' : 'groups'} ready to build`
                  : 'No groups awaiting build'}
              </span>
            </span>
            {queueCount > 0 ? (
              <span className='ml-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-xs font-bold tabular-nums text-primary-foreground'>
                {queueCount}
              </span>
            ) : null}
            <span className='ml-auto inline-flex items-center gap-1 text-[12px] font-semibold text-primary'>
              Review &amp; build <ArrowRight className='h-3.5 w-3.5' />
            </span>
          </button>

          <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
            {groups.map((group) => {
              // Bucket total = sum of member stage totals (each stage total
              // already counts a shared canonical value only once).
              const groupTotal = group.stages.reduce(
                (sum, stage) => sum + (stageCounts[stage.key] ?? 0),
                0,
              );
              return (
                <div key={group.key} className='rounded-lg border bg-background p-3'>
                  <div className='flex items-baseline justify-between gap-1 mb-2'>
                    <span className='text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground truncate'>
                      {group.label}
                    </span>
                    <span
                      className={`text-[20px] font-bold leading-none tabular-nums shrink-0 ${
                        groupTotal > 0 ? '' : 'text-muted-foreground'
                      }`}
                    >
                      {groupTotal}
                    </span>
                  </div>
                  <div className='space-y-0.5'>
                    {group.stages.flatMap((stage) =>
                      stage.subStages.map((sub) => {
                        const count = subStageCounts[sub.key] ?? 0;
                        const isActive = selected.has(sub.key);
                        const tone = cbsStatusTone(sub.systemSubStage, stage.systemPhase);
                        const lit = count > 0 || isActive;
                        return (
                          <button
                            key={sub.key}
                            type='button'
                            title={sub.label}
                            onClick={() => onToggle(sub.key)}
                            aria-pressed={isActive}
                            className='w-full flex items-center justify-between rounded px-1.5 py-1 text-left transition-colors hover:brightness-95'
                            style={{backgroundColor: isActive ? tone.bg : 'transparent'}}
                          >
                            <span
                              className={`text-xs truncate ${
                                lit ? 'font-semibold' : 'text-muted-foreground'
                              }`}
                              style={lit ? {color: tone.fg} : undefined}
                            >
                              {sub.label}
                            </span>
                            <span
                              className={`text-xs font-bold tabular-nums ml-2 shrink-0 ${
                                lit ? '' : 'text-muted-foreground'
                              }`}
                              style={lit ? {color: tone.fg} : undefined}
                            >
                              {count}
                            </span>
                          </button>
                        );
                      }),
                    )}
                  </div>
                </div>
              );
            })}

            {activeLanes.map((stage) => {
              // The ACTIVE stage maps to a single sub-stage in practice; the
              // tile toggles every sub-stage key it carries so filtering stays
              // correct even for exotic configs.
              const isActive = stage.subStages.some((sub) => selected.has(sub.key));
              return (
                <button
                  key={stage.key}
                  type='button'
                  onClick={() => stage.subStages.forEach((sub) => onToggle(sub.key))}
                  aria-pressed={isActive}
                  className='flex min-h-[120px] flex-col justify-between rounded-lg p-3 text-left transition-all hover:brightness-95'
                  style={{
                    backgroundColor: CBS_LIVE_TILE.bg,
                    color: CBS_LIVE_TILE.text,
                    border: `1.5px solid ${isActive ? CBS_LIVE_TILE.text : 'transparent'}`,
                  }}
                >
                  <span className='flex items-center gap-1.5'>
                    <span
                      className='h-2 w-2 rounded-full'
                      style={{backgroundColor: CBS_LIVE_TILE.text}}
                    />
                    <span className='text-xs font-semibold uppercase tracking-[0.08em]'>
                      {stage.label}
                    </span>
                  </span>
                  <span>
                    <span className='block text-[30px] font-bold leading-none tabular-nums'>
                      {stageCounts[stage.key] ?? 0}
                    </span>
                    <span className='mt-1 block text-xs'>active book</span>
                  </span>
                </button>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}
